import Spacer from "@components/Spacer";
import CustomImage from "@components/custom-image";
import SafeHorizontal from "@components/safe-horizontal";
import type { CustomListViewType } from "@features/product/custom-listview.shared";
import { capitalizeFirstLetter, debounce } from "@utils";
import { Container, H2, Text, View } from "dripsy";
import * as React from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";

import { extractKey, RenderComponent, splitChunks } from "./custom-listview.shared";
import { forwardWebScroll } from "./web-scroll";
import noResultsImage from "../../assets/empty-results.png";
import { LoadingCallback } from "@components/async-boundary";
// import Footer from "@components/footer";

const STICKY_HEADER_HEIGHT = 50;

function CustomListview<ItemT = any>({
  renderItem,
  data,
  itemHeight,
  scrollHandler,
  px = 0,
  gap = 0,
  numColumns = 1,
  style,
  pt = 0,
  emptyText = "Helaas waren er geen resultaten",
  onEndReached,
  withContainer,
  scrollToTopIfChanged,
  isLoadingMore,
}: CustomListViewType<ItemT>) {
  const newData = React.useMemo(() => splitChunks(data, numColumns), [data, numColumns]);
  const virtuoso = React.useRef<VirtuosoHandle>(null);
  React.useEffect(() => {
    if (virtuoso.current && scrollToTopIfChanged) {
      virtuoso.current.scrollTo({
        left: 0,
        top: 0,
      });
    }
  }, [data, scrollToTopIfChanged]);
  // Keep the latest handler in a ref: lists like the categories screen pass no
  // scrollHandler at all, and the callback below must not capture a stale one.
  const scrollHandlerRef = React.useRef(scrollHandler);
  scrollHandlerRef.current = scrollHandler;
  const onScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    // get content offset of target
    const offsetY = e.currentTarget.scrollTop;
    forwardWebScroll(
      () => scrollHandlerRef.current,
      offsetY,
      (fn) => debounce(fn, "list-scroll", 150)
    );
  }, []);
  return (
    <Virtuoso
      ref={virtuoso}
      defaultItemHeight={itemHeight}
      endReached={onEndReached}
      onScrollCapture={onScroll}
      atBottomThreshold={200}
      components={{
        // Footer,
        Header: () => {
          return <View sx={{ height: pt }} />;
        },
        Footer: isLoadingMore ? LoadingCallback : null,
        EmptyPlaceholder: () => {
          return (
            <Container sx={{ alignItems: "center", pt: 48 + pt }}>
              <CustomImage
                source={noResultsImage as any}
                sx={{ height: 60, width: 100 }}
                contentFit="contain"
                alt="Geen resultaten"
              />
              <Spacer />
              <Text sx={{ fontSize: 18, fontWeight: "500" }}>{emptyText}</Text>
            </Container>
          );
        },
      }}
      style={{ ...(style as any), height: "100%" }}
      data={newData}
      computeItemKey={(index) => {
        const item = newData[index];
        return extractKey(item);
      }}
      itemContent={(chunkIndex, item) => {
        const header = typeof item === "string";

        if (header) {
          return <StickyHeader name={item as any} isListItem />;
        }

        return (
          <RenderComponent
            px={px}
            chunkIndex={chunkIndex}
            chunkSize={data.length}
            gap={gap}
            item={item as ItemT[]}
            numColumns={numColumns}
            renderItem={renderItem}
            withContainer={withContainer}
          />
        );
      }}
    />
  );
}

export default CustomListview as typeof CustomListview;

const StickyHeader = ({ name, isListItem = false }: { name: string; isListItem?: boolean }) => {
  if (isListItem) {
    return (
      <SafeHorizontal
        sx={{
          flex: 1,
          backgroundColor: "$background",
          height: STICKY_HEADER_HEIGHT,
          justifyContent: "flex-end",
          pb: "$2",
        }}
      >
        <H2 sx={{ fontSize: [16, 18, 20], fontWeight: "600", px: "$2", mb: 0, mt: 0 }}>
          {capitalizeFirstLetter(name.toLowerCase())}
        </H2>
      </SafeHorizontal>
    );
  }
  return (
    <SafeHorizontal
      sx={{ backgroundColor: "$background", height: STICKY_HEADER_HEIGHT, justifyContent: "flex-end", pb: "$2" }}
    >
      <H2 sx={{ fontSize: [16, 18, 20], fontWeight: "600", px: "$2", mb: 0, mt: 0 }}>
        {capitalizeFirstLetter(name.toLowerCase())}
      </H2>
    </SafeHorizontal>
  );
};
